import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Terms of </span>
          <span className="text-[#00FF41]">Service</span>
        </h1>
        <p className="text-gray-500 text-sm mb-8">Last updated: May 14, 2026</p>

        <div className="space-y-6 text-sm text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. Acceptance of Terms</h2>
            <p>By accessing or using BitBot ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service. We reserve the right to modify these Terms at any time, and your continued use following any changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. Description of Service</h2>
            <p>BitBot is an automated cryptocurrency trading platform that connects to your Coinbase exchange account to execute trades based on technical indicators (RSI, EMA) and user-configured strategies. The Service includes simulation mode (paper trading), live trading, portfolio tracking, notifications, leaderboards, and an AI assistant.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. Financial Risk Disclaimer</h2>
            <p className="text-yellow-500 font-semibold">CRYPTOCURRENCY TRADING INVOLVES SUBSTANTIAL RISK OF LOSS AND IS NOT SUITABLE FOR EVERY INVESTOR. THE VALUATION OF CRYPTOCURRENCIES MAY FLUCTUATE, AND AS A RESULT, YOU MAY LOSE MORE THAN YOUR ORIGINAL INVESTMENT. PAST PERFORMANCE IS NOT INDICATIVE OF FUTURE RESULTS.</p>
            <p className="mt-2">BitBot is a software tool, not a financial advisor, broker-dealer, or investment advisor. The Service does not provide investment advice, and no information provided through the Service should be construed as such. You are solely responsible for all trading decisions made through the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. User Responsibilities</h2>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>You must be at least 18 years old to use the Service</li>
              <li>You are responsible for maintaining the security of your account and API keys</li>
              <li>You are solely responsible for all trades executed through your account</li>
              <li>You must comply with all applicable laws regarding cryptocurrency trading in your jurisdiction</li>
              <li>You must not use the Service for money laundering, market manipulation, or any illegal activity</li>
              <li>You understand that bot performance depends on market conditions and configuration settings</li>
              <li>You must ensure you have sufficient funds in your exchange account for the trades you configure</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">5. Exchange API Access</h2>
            <p>The Service requires API access to your Coinbase account to function. By providing API credentials, you authorize BitBot to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400 mt-2">
              <li>View your account balances and portfolio</li>
              <li>Place buy and sell orders on your behalf</li>
              <li>Access your order and trade history</li>
            </ul>
            <p className="mt-2">You can revoke API access at any time through your exchange account settings. We recommend using API keys with trading-only permissions (no withdrawal access).</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">6. Simulation Mode</h2>
            <p>Simulation mode uses virtual funds and does not execute real trades. Performance in simulation mode does not guarantee similar performance in live trading. Market conditions, fees, slippage, and liquidity can significantly affect live trading results.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">7. Fees</h2>
            <p>BitBot itself may charge subscription or usage fees as described on our pricing page. Additionally, all trades executed through your exchange account are subject to the exchange's trading fees (e.g., Coinbase taker/maker fees). These exchange fees are not controlled by BitBot and are deducted directly by the exchange.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">8. Service Availability</h2>
            <p>We strive to maintain continuous service but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, exchange outages, network issues, or circumstances beyond our control. Trading bots may be interrupted during service restarts.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">9. Disclaimer of Warranties</h2>
            <p className="uppercase text-gray-400">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL GENERATE PROFITS, BE UNINTERRUPTED, SECURE, OR ERROR-FREE.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">10. Limitation of Liability</h2>
            <p className="uppercase text-gray-400">TO THE FULLEST EXTENT PERMITTED BY LAW, BITBOT AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY TRADING LOSSES, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, OR CRYPTOCURRENCY RESULTING FROM YOUR USE OF THE SERVICE.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">11. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless BitBot and its affiliates from any claims, liabilities, damages, losses, and expenses arising from your use of the Service, your trading activities, your violation of these Terms, or your violation of any third-party rights.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">12. Governing Law</h2>
            <p>These Terms shall be governed by the laws of the United States and the State of California, without regard to conflict of law provisions.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">13. Contact</h2>
            <p><span className="text-white font-bold">BitBot</span><br/>Email: <a href="mailto:eviltim@gmail.com" className="text-[#00FF41] hover:text-white">eviltim@gmail.com</a><br/>Website: <a href="https://xbitbotx.com" className="text-[#00FF41] hover:text-white">xbitbotx.com</a></p>
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#003B00]/30 flex flex-wrap gap-4 text-xs text-gray-600">
          <a href="/about" className="hover:text-[#00FF41]">About</a>
          <a href="/privacy" className="hover:text-[#00FF41]">Privacy Policy</a>
          <a href="/faq" className="hover:text-[#00FF41]">FAQ</a>
          <a href="/changelog" className="hover:text-[#00FF41]">Changelog</a>
        </footer>
      </div>
    </div>
  );
}
